import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';

const CustomerForm = ({ customer, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    name: '',
    addressLine1: '',
    addressLine2: '',
    locality: '',
    city: '',
    state: '',
    pincode: '',
    mobile: '',
    email: '',
    relationship: 'CUSTOMER',
    gst: ''
  });
  const [errors, setErrors] = useState({});

  // Populate form data when customer prop changes
  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        addressLine1: customer.addressLine1 || '',
        addressLine2: customer.addressLine2 || '',
        locality: customer.locality || '',
        city: customer.city || '',
        state: customer.state || '',
        pincode: customer.pincode || '',
        mobile: customer.phone || '',
        email: customer.email || '',
        relationship: customer.relationship || 'CUSTOMER',
        gst: customer.gst || ''
      });
    } else {
      // Reset form for new customer
      setFormData({
        name: '',
        addressLine1: '',
        addressLine2: '',
        locality: '',
        city: '',
        state: '',
        pincode: '',
        mobile: '',
        email: '',
        relationship: 'CUSTOMER',
        gst: ''
      });
    }
  }, [customer]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = 'Address line 1 is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }

    // Validate mobile format (basic validation) - only if provided and not "-"
    if (formData.mobile && formData.mobile !== '-' && formData.mobile.trim() !== '') {
      if (!/^[6-9]\d{9}$/.test(formData.mobile)) {
        newErrors.mobile = 'Invalid mobile number format';
      }
    }

    // Validate email format - only if provided and not "-"
    if (formData.email && formData.email !== '-' && formData.email.trim() !== '') {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group className="mb-3">
        <Form.Label>Name <span className="text-danger">*</span></Form.Label>
        <Form.Control
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          isInvalid={!!errors.name}
          placeholder="Enter customer name"
        />
        <Form.Control.Feedback type="invalid">
          {errors.name}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Address Line 1 <span className="text-danger">*</span></Form.Label>
        <Form.Control
          type="text"
          name="addressLine1"
          value={formData.addressLine1}
          onChange={handleChange}
          isInvalid={!!errors.addressLine1}
          placeholder="Enter address line 1"
        />
        <Form.Control.Feedback type="invalid">
          {errors.addressLine1}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Address Line 2</Form.Label>
        <Form.Control
          type="text"
          name="addressLine2"
          value={formData.addressLine2}
          onChange={handleChange}
          placeholder="Enter address line 2 (optional)"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Locality</Form.Label>
        <Form.Control
          type="text"
          name="locality"
          value={formData.locality}
          onChange={handleChange}
          placeholder="Enter locality"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>City <span className="text-danger">*</span></Form.Label>
        <Form.Control
          type="text"
          name="city"
          value={formData.city}
          onChange={handleChange}
          isInvalid={!!errors.city}
          placeholder="Enter city"
        />
        <Form.Control.Feedback type="invalid">
          {errors.city}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>State <span className="text-danger">*</span></Form.Label>
        <Form.Control
          type="text"
          name="state"
          value={formData.state}
          onChange={handleChange}
          isInvalid={!!errors.state}
          placeholder="Enter state"
        />
        <Form.Control.Feedback type="invalid">
          {errors.state}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Pincode</Form.Label>
        <Form.Control
          type="text"
          name="pincode"
          value={formData.pincode}
          onChange={handleChange}
          placeholder="Enter pincode"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Mobile</Form.Label>
        <Form.Control
          type="tel"
          name="mobile"
          value={formData.mobile}
          onChange={handleChange}
          isInvalid={!!errors.mobile}
          placeholder="Enter mobile number or '-' if not available"
        />
        <Form.Control.Feedback type="invalid">
          {errors.mobile}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Email</Form.Label>
        <Form.Control
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          isInvalid={!!errors.email}
          placeholder="Enter email address or '-' if not available"
        />
        <Form.Control.Feedback type="invalid">
          {errors.email}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>GST Number</Form.Label>
        <Form.Control
          type="text"
          name="gst"
          value={formData.gst}
          onChange={handleChange}
          placeholder="Enter GST number (optional)"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Relation to Company</Form.Label>
        <Form.Select
          name="relationship"
          value={formData.relationship}
          onChange={handleChange}
        >
          <option value="CUSTOMER">CUSTOMER</option>
          <option value="SUPPLIER">SUPPLIER</option>
          <option value="VENDOR">VENDOR</option>
          <option value="PARTNER">PARTNER</option>
          <option value="OTHER">OTHER</option>
        </Form.Select>
      </Form.Group>

      <div className="d-flex gap-2 justify-content-end">
        <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={isLoading}>
          {isLoading && <Spinner animation="border" size="sm" className="me-2" />}
          {customer ? 'Update Customer' : 'Add Customer'}
        </Button>
      </div>
    </Form>
  );
};

export default CustomerForm;